<template>
  <div class="ops-card">
    <div class="ops-card__head">
      <span class="ops-card__title">{{ title }}</span>
      <span class="ops-card__note">{{ note }}</span>
      <span v-if="!loading" class="ops-card__count">{{ rows.length }}</span>
    </div>
    <div v-if="loading" class="ops-card__status"><a-spin size="small" /></div>
    <div v-else-if="error" class="ops-card__status">{{ error }}</div>
    <div v-else-if="rows.length === 0" class="ops-card__status">No rows</div>
    <div v-else class="ops-card__table" :class="{ 'ops-card__table--scroll': rows.length > 10 }">
      <a-table
        :dataSource="rows"
        :columns="columns"
        :pagination="false"
        size="small"
        rowKey="_k"
      />
    </div>
  </div>
</template>

<script>
  import AdminService from '../services/admin.service';

  const COLUMN_LABELS = {
    period: '',
    hr: 'Hour SGT',
    no_detail: 'No detail',
    s403: '403',
    s404: '404',
    s5xx: '5xx',
    non_200: 'Non-200',
    custCustomerID: 'Customer',
    custUsername: 'Name',
    urls: 'URLs',
    urlID: 'URL',
    urlCustomerID: 'Customer',
    urlUrlName: 'URL',
    alerts: 'Alerts',
    hours_alerting: 'Hours',
    runs: 'Runs',
    avg_fetch_s: 'Fetch s',
    custStatus: 'Status',
    stale_urls: 'Stale',
    oldest_verify: 'Oldest check',
    total_s: 'Total',
    content_s: 'Content',
    integrity_s: 'Integrity',
    image_s: 'Image',
    malware_s: 'Malware',
    nlp_s: 'NLP',
    urlHealthStatus: 'Status',
    n: 'Count',
    pct: 'Share %',
    urlLastBaseline: 'Last baseline',
    mins: 'Minutes',
    expected_checks_per_hr: 'Checks / hour',
    custExpiryDate: 'Expiry',
    live_urls: 'Live URLs',
    d: 'Day',
    bounceEmailType: 'Type',
    addresses: 'Addresses',
  };

  function columnLabel(name) {
    return COLUMN_LABELS[name] || name.replace(/_/g, ' ');
  }

  function engineTimeRows(row) {
    const engines = ['total_s', 'content_s', 'integrity_s', 'image_s', 'malware_s', 'nlp_s'];
    const lastHour = { period: 'Last hour', _above: {} };
    const weekAvg = { period: 'Week avg' };
    engines.forEach((name) => {
      lastHour[name] = row[name];
      weekAvg[name] = row[name + '_prev_7d'];
      const hasBoth = row[name] !== null && weekAvg[name] !== null && weekAvg[name] !== undefined;
      lastHour._above[name] = hasBoth && Number(row[name]) > Number(weekAvg[name]);
    });
    return [lastHour, weekAvg];
  }

  export default {
    name: 'OpsReportCard',
    props: {
      // Must be one of the names in the backend ops_report route.
      report: { type: String, required: true },
      title: { type: String, required: true },
      note: { type: String, default: '' },
    },
    data() {
      return {
        loading: true,
        error: '',
        rows: [],
      };
    },
    computed: {
      columns() {
        const rows = this.rows;
        if (!rows.length) {
          return [];
        }
        return Object.keys(rows[0])
          .filter((name) => name !== '_k' && name !== '_above')
          .map((name) => {
            const numeric = rows.every((row) => row[name] === null || row[name] === '' || !isNaN(Number(row[name])));
            return {
              title: columnLabel(name),
              dataIndex: name,
              key: name,
              align: numeric ? 'right' : 'left',
              customCell: (row) => (row._above && row._above[name] ? { class: 'ops-card__cell--above' } : {}),
            };
          });
      },
    },
    mounted() {
      this.load();
    },
    methods: {
      async load() {
        try {
          const res = await AdminService.fetchOpsReport(this.report);
          let rows = (res && res.rows) || [];
          if (this.report === 'engine_time' && rows.length) {
            rows = engineTimeRows(rows[0]);
          }
          this.rows = rows.map((row, index) => ({ ...row, _k: index }));
          this.error = (res && res.error) || '';
        } catch (error) {
          this.error = 'This report did not load.';
        }
        this.loading = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ops-card {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    padding: 8px 10px 4px;
    min-width: 0;
  }

  .ops-card__head {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 4px;
  }

  .ops-card__title {
    font-size: 13px;
    font-weight: 600;
    color: #323232;
    white-space: nowrap;
  }

  .ops-card__note {
    color: #858688;
    font-size: 12px;
  }

  .ops-card__count {
    margin-left: auto;
    min-width: 18px;
    padding: 0 6px;
    border-radius: 10px;
    background: #f4f5f7;
    color: #565656;
    font-size: 11px;
    line-height: 18px;
    text-align: center;
  }

  .ops-card__status {
    color: #858688;
    font-size: 12px;
    padding: 12px 0;
  }

  .ops-card__table {
    overflow-x: auto;
  }

  .ops-card__table--scroll {
    max-height: 320px;
    overflow: auto;
  }

  .ops-card__table ::v-deep .ant-table {
    font-size: 12px;
    color: #323232;
  }

  .ops-card__table ::v-deep table {
    width: max-content;
    min-width: 100%;
    table-layout: auto;
  }

  .ops-card__table ::v-deep .ant-table-thead > tr > th,
  .ops-card__table ::v-deep .ant-table-tbody > tr > td {
    padding: 4px 8px;
    white-space: nowrap;
  }

  .ops-card__table ::v-deep .ant-table-thead > tr > th {
    background: #fafafa;
    color: #565656;
    font-weight: 600;
  }

  .ops-card__table ::v-deep .ant-table-tbody > tr:hover > td {
    background: #f7fbfc;
  }

  .ops-card__table ::v-deep .ant-table-tbody > tr > td.ops-card__cell--above {
    color: #cf1322;
    font-weight: 600;
  }
</style>
